import React from 'react';
import Avatar from '../../components/Avatar';
import Card from '../../components/Card';
import Metrics from '../../components/Metrics';
import OpenContactLink from '../../components/OpenContactLink/OpenContactLink';
import Skeleton from '../../components/Skeleton/Skeleton';
import Tenant from '../../components/Tenant';
import { variables } from 'src/Tenants';
import Callout from 'src/components/InformationCallout/Callout';
import Button from 'src/components/Button';
import NewBadge from 'src/components/NewBadge/NewBadge';

interface ContactNavOrigin {
  kind: 'property';
  propertyId: string;
  propertyName: string;
}

interface PropertyContact {
  id: string;
  firstname?: string;
  lastname?: string;
  profileImage?: string;
  phone?: string;
}

interface PropertyContactCardProps {
  title: string;
  contact?: PropertyContact;
  badge: string;
  linkView: boolean;
  navOrigin: ContactNavOrigin;
  strapline?: React.ReactNode;
  onAvatarClick: (image: string) => void;
}

interface PropertyDetailItemsParams {
  property: any;
  counts: any;
  srVisibleCount: number;
  serviceRequestLabel?: string;
  linkView: boolean;
  onAvatarClick: (image: string) => void;
  onManageStrataManagers: () => void;
}

const PropertyContactCard = ({
  title,
  contact,
  badge,
  linkView,
  navOrigin,
  strapline = '',
  onAvatarClick,
}: PropertyContactCardProps) => {
  if (!contact || !contact.firstname) {
    return (
      <Card
        title={title}
        body={<div className="property-card-unassigned">Unassigned</div>}
      />
    );
  }

  const fullName: string = `${contact.firstname} ${contact.lastname}`;

  return (
    <Card
      title={title}
      body={
        <div className="property-contact-card-row">
          <Tenant
            headline={fullName}
            strapline={strapline}
            avatar={
              <div onClick={() => onAvatarClick(contact.profileImage)}>
                <Avatar badge={badge} url={contact.profileImage} />
              </div>
            }
          />

          {linkView && (
            <OpenContactLink
              userId={contact.id}
              name={fullName}
              navOrigin={navOrigin}
            />
          )}
        </div>
      }
    />
  );
};

export const getPropertyDetailItems = ({
  property,
  counts,
  srVisibleCount,
  serviceRequestLabel,
  linkView,
  onAvatarClick,
  onManageStrataManagers,
}: PropertyDetailItemsParams): React.ReactNode[] => {
  const navOrigin: ContactNavOrigin = {
    kind: 'property',
    propertyId: property.id ?? property.propertyId,
    propertyName: property.name,
  };

  return [
    <Card
      body={
        <>
          {property.addressLine1}
          <br />
          {property.city}, {property.province}
          <br />
          {property.postalCode}
        </>
      }
    />,
    <Metrics
      title="Overview"
      items={[
        {
          key: variables.suiteName,
          value: counts.suitesCount ? counts.suitesCount : 0,
        },
        {
          key: variables.homeOwners,
          value: counts.homeOwnersCount ? counts.homeOwnersCount : 0,
        },
        {
          key: serviceRequestLabel,
          value: srVisibleCount,
        },
      ]}
    />,
    <PropertyContactCard
      title={'Primary ' + variables.propertyDeveloper}
      contact={property.propertyManager}
      badge="property-manager"
      linkView={linkView}
      navOrigin={navOrigin}
      onAvatarClick={onAvatarClick}
    />,
    <Callout
      variant="info"
      title={
        <>
          Manage {variables.strataManagers}
          <NewBadge spaced />
        </>
      }
      subtitle={`Assign ${variables.strataManagers} to common areas on this property — a common area can have more than one.`}
    >
      <Button color="teal" size="small" onClick={onManageStrataManagers}>
        Open
      </Button>
    </Callout>,
  ];
};

export const getPropertyDetailSkeletonItems = (): React.ReactNode[] => {
  return [
    <Skeleton variant="rect" width="14rem" height="6rem" />,
    <Skeleton variant="rect" width="16rem" height="6rem" />,
    <Skeleton variant="rect" width="16rem" height="6rem" />,
    <Skeleton variant="rect" width="16rem" height="6rem" />,
  ];
};